// libs
import React, { memo, useState, useEffect, useMemo } from 'react';

// helpers
import { getZoomStyle, getPositionStyle } from './helpers';

// constants
import { PROBE_HEIGHT, PROBE_WIDTH, ZOOM_HEIGHT, ZOOM_WIDTH } from './constants';

// styles
import { ZoomContainer } from './styles';

// defs
interface IDesktopImageZoomProps {
  zoomElRef: React.RefObject<HTMLDivElement>;
  imageSrc: string;
}

const ZOOM_SCALE_X = ZOOM_WIDTH / PROBE_WIDTH;
const ZOOM_SCALE_Y = ZOOM_HEIGHT / PROBE_HEIGHT;

function DesktopImageZoom({ zoomElRef, imageSrc }: IDesktopImageZoomProps) {
  const [dimension, setDimension] = useState({ width: 0, height: 0, top: 0, left: 0 });
  const [visualDimension, setVisualDimension] = useState({
    width: 0,
    height: 0,
    top: 0,
    left: 0,
  });
  const [showProbe, setShowProbe] = useState(false);
  const [imageLoaded, setImageLoaded] = useState('');
  const [imagePosition, setImagePosition] = useState({ left: 0, top: 0 });
  const [probePosition, setProbePosition] = useState({ left: 0, top: 0 });
  let rAFTimer: number | null = null;

  useEffect(() => {
    if (zoomElRef.current === null) {
      return;
    }

    const { width, height, top: elTop, left: elLeft } = zoomElRef.current.getBoundingClientRect();
    const top = window.pageYOffset + elTop;
    const left = window.pageXOffset + elLeft;

    if (
      dimension.width !== width ||
      dimension.height !== height ||
      dimension.top !== top ||
      dimension.left !== left
    ) {
      setDimension({ width, height, top, left });
    }
  }, [zoomElRef, dimension]);

  useEffect(() => {
    if (dimension.height === 0 || dimension.width === 0) {
      return;
    }

    if (imageSrc === imageLoaded) {
      return;
    }

    const img = new Image();
    let isValidImageLoad = true;

    const onImageLoad = () => {
      if (isValidImageLoad === false) {
        return;
      }

      setImageLoaded(imageSrc);
      const { naturalHeight: imgHeight, naturalWidth: imgWidth } = img;

      // calculate visible image dimension for overlay and zoom
      // as image is rendered using CSS contain
      let height = dimension.height;
      let width = dimension.width;
      let top = dimension.top;
      let left = dimension.left;

      if (imgHeight > imgWidth) {
        // calculate width and overlay left with respect to image aspect ratio
        width = (height * imgWidth) / imgHeight;
        left = left + (dimension.width - width) / 2;
      } else {
        // calculate height and overlay top with respect to image aspect ratio
        height = (width * imgHeight) / imgWidth;
        top = top + (dimension.height - height) / 2;
      }

      setVisualDimension({ top, left, height, width });
    };

    img.onload = onImageLoad;
    img.src = imageSrc;

    return () => {
      isValidImageLoad = false;
    };
  }, [imageSrc, imageLoaded, dimension.height, dimension.width, dimension.top, dimension.left]);

  const onMouseMove: React.MouseEventHandler = (e) => {
    const { clientX, clientY } = e;

    if (rAFTimer !== null) {
      window.cancelAnimationFrame(rAFTimer);
    }

    rAFTimer = window.requestAnimationFrame(() => {
      rAFTimer = null;

      let vTop = visualDimension.top;
      // as image is sticky - we would need to calculate top everytime
      // based on container node
      if (zoomElRef.current !== null) {
        vTop = zoomElRef.current.getBoundingClientRect().top;
      }

      let newLeft = clientX - visualDimension.left - PROBE_WIDTH / 2;
      let newTop = clientY - vTop - PROBE_HEIGHT / 2;

      if (newLeft < 0) {
        newLeft = 0;
      }

      if (newLeft + PROBE_WIDTH > visualDimension.width) {
        newLeft = visualDimension.width - PROBE_WIDTH;
      }

      if (newTop < 0) {
        newTop = 0;
      }

      if (newTop + PROBE_HEIGHT > visualDimension.height) {
        newTop = visualDimension.height - PROBE_HEIGHT;
      }

      if (newLeft === probePosition.left && newTop === probePosition.top) {
        return;
      }

      setProbePosition({ top: newTop, left: newLeft });
      setImagePosition({
        top: -1 * newTop * ZOOM_SCALE_Y,
        left: -1 * newLeft * ZOOM_SCALE_X,
      });
    });
  };

  const onMouseEnter = () => setShowProbe(true);
  const onMouseLeave = () => setShowProbe(false);

  const zoomStyle = useMemo(() => getZoomStyle(visualDimension), [visualDimension]);
  const probeStyle = useMemo(() => getPositionStyle(probePosition), [probePosition]);
  const imageStyle = useMemo(() => getPositionStyle(imagePosition), [imagePosition]);

  const showZoomed = showProbe && imageLoaded === imageSrc;

  return (
    <ZoomContainer
      style={zoomStyle}
      onMouseEnter={onMouseEnter}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      data-test="zoom-container"
    >
      {showZoomed && (
        <>
          <div className="zm-probe" style={probeStyle} />
          <div className="zm-image-cnt">
            <img
              src={imageSrc}
              className="zm-image"
              style={imageStyle}
              height={ZOOM_SCALE_Y * visualDimension.height}
              width={ZOOM_SCALE_X * visualDimension.width}
              alt=""
              data-test="zoom-contianer-img"
            />
          </div>
        </>
      )}
    </ZoomContainer>
  );
}

export default memo(DesktopImageZoom);
